import React, { useState } from "react";
import styled from "styled-components";
import { Grid2 as Grid, Skeleton } from "@mui/material";
import {
  ArrowForwardOrange,
  BlankImage
} from "../../../shared/icons";
import { IconButtonSmall } from "../../../components/ButtonsNew";
import Loader from "../../../components/shared-components/Loader";
import { screenSizes } from "../../../styles/theme";
import { useNavigate } from "react-router-dom";
import { getValidImageUrl } from "../../../utils";

const FeaturedCollectionContainer = styled.section`
  margin: 80px 0;
  padding: 0 80px;
  @media (max-width: ${screenSizes.L}px) {
    margin-top: 30px;
    padding: 0 20px;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 40px;
`;

const TitleContainer = styled.div`
  color: #091c00;
  h2 {
    font-family: "Manrope", sans-serif;
    font-size: 50px;
    font-weight: 700;
    margin: 0;
    letter-spacing: -2px;
    @media (max-width: ${screenSizes.L}px) {
      font-size: 32px;
      padding-bottom: 5px;
      letter-spacing: 0px;
    }
  }

  h5 {
    font-family: "Manrope", sans-serif;
    color: rgba(9, 28, 0, 0.8);
    font-size: 18px;
    font-weight: 400;
    margin: 0;
    @media (max-width: ${screenSizes.L}px) {
      font-size: 16px;
    }
  }
`;

const Card = styled.div`
  cursor: pointer;
  padding: 12px;
  background-color: #fff;
  border: 1px solid rgba(9, 28, 0, 0.1);
  background-color: #fff;
  border-bottom-right-radius: 40px;
  img {
    height: 373px;
    width: 100%;
    margin-bottom: 10px;
    background-size: cover;
    background-position: center;
  }
  h5 {
    margin: 0;
    font-family: "Manrope", sans-serif;
    font-size: 24px;
    font-weight: 700;
    color: rgba(9, 28, 0, 1);
    margin-bottom: 5px;
    display: -webkit-box; //  box layout for clamping
    -webkit-line-clamp: 1; // Limit to 2 lines
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    word-wrap: break-word;
  }
  div {
    display: flex;
    align-items: flex-start;
    column-gap: 20px;
  }
`;

const FloorPrice = styled.div`
  display: flex;
  flex-direction: column;

  h6 {
    margin: 0;
    font-family: "Manrope", sans-serif;
    font-size: 16px;
    font-weight: 500;
    color: rgba(9, 28, 0, 0.6);
  }

  h5 {
    margin: 0;
    font-family: "Manrope", sans-serif;
    font-size: 20px;
    font-weight: 700;
    color: rgba(9, 28, 0, 1);
  }
`;

const Volume = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  h6 {
    margin: 0;
    font-family: "Manrope", sans-serif;
    font-size: 16px;
    font-weight: 500;
    color: rgba(9, 28, 0, 0.6);
  }

  h5 {
    margin: 0;
    font-family: "Manrope", sans-serif;
    font-size: 20px;
    font-weight: 700;
    color: rgba(9, 28, 0, 1);
  }
`;

const ButtonContainer = styled.div`
  margin-top: 40px;
  text-align: center;
`;

const LoaderWrapper = styled.div`
  margin-top: 15px;
`;

const NoResults = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 15px;
`;

type FeaturedCollections = {
  id: string;
  name: string;
  floorPrice: string | number;
  volume: string | number;
};

interface Props {
  collections: FeaturedCollections[] | [];
  isLoading?: boolean;
  currentPage: number;
  totalPages: number;
  getMoreCollections: (
    limit: number,
    pageNo: number,
    isLoadingMore: boolean
  ) => void;
  isLoadingMore: boolean;
  loadMoreFeaCollections: () => void;
}

const FeaturedCollectionSection: React.FC<Props> = ({
  collections,
  isLoading,
  currentPage,
  totalPages,
  getMoreCollections,
  isLoadingMore,
  loadMoreFeaCollections,
}) => {
  const navigate = useNavigate();
  const [imageLoaded, setImageLoaded] = useState(false);

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleLoadMore = () => {
    loadMoreFeaCollections();
  };

  const handleViewAll = () => {
    const offset = window.innerHeight;
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  };

  return (
    <FeaturedCollectionContainer>
      {/* header */}
      <HeaderContainer>
        <TitleContainer>
          <h2>Featured Projects & Collections</h2>
          <h5>
          Curated projects that stand out for their impact, topic, or aesthetics.
          </h5>
        </TitleContainer>
      </HeaderContainer>
      {/* cards */}
      <Grid container spacing={2}>
        {isLoading ? (
          <LoaderWrapper>
            <Loader />
          </LoaderWrapper>
        ) : collections?.length > 0 ? (
          collections?.map((item: any) => (
            <Grid size={{ xs: 12, md: 4 }}>
              <Card onClick={() => navigate(`/collection-details/${item.id}`)}>
                {!imageLoaded && (
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height={200}
                    animation="wave"
                    sx={{
                      marginBottom: 2,
                    }}
                  />
                )}
                <img
                  src={getValidImageUrl(item?.image, BlankImage)}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = BlankImage;
                  }}
                  alt="collection_image"
                  style={{ display: imageLoaded ? "block" : "none" }}
                  onLoad={handleImageLoad}
                />
                <h5 title={item?.name}>{item?.name}</h5>
                <div>
                  <FloorPrice>
                    <h6>Floor Price</h6>
                    <h5>
                      {parseFloat(item?.floorPrice || 0).toFixed(4)}{" "}
                      {item?.chain === "hedera"
                        ? "HBAR"
                        : item?.chain === "ethereum"
                        ? "ETH"
                        : "POL"}{" "}
                    </h5>
                  </FloorPrice>
                  <Volume>
                    <h6>Volume</h6>
                    <h5>
                      {item?.volume}{" "}
                      {item?.chain === "hedera"
                        ? "HBAR"
                        : item?.chain === "ethereum"
                        ? "ETH"
                        : "POL"}{" "}
                    </h5>
                  </Volume>
                </div>
              </Card>
            </Grid>
          ))
        ) : (
          <NoResults>No Results Found!</NoResults>
        )}
      </Grid>

      {isLoadingMore && (
        <LoaderWrapper>
          <Loader />
        </LoaderWrapper>
      )}

      {/* action button */}
      {collections?.length < 6 ? (
        <ButtonContainer>
          <IconButtonSmall
            icon={<ArrowForwardOrange />}
            onClick={handleLoadMore}
          >
            View More
          </IconButtonSmall>
        </ButtonContainer>
      ) : (
        !isLoading && (
          <ButtonContainer>
            <IconButtonSmall
              icon={<ArrowForwardOrange />}
              onClick={handleViewAll}
            >
              View All
            </IconButtonSmall>
          </ButtonContainer>
        )
      )}
    </FeaturedCollectionContainer>
  );
};

export default FeaturedCollectionSection;
